import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "kj_admin_token";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [user, setUser] = useState(null); // null = unknown, {username} = logged in, false = anon
  const [checking, setChecking] = useState(!!token);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setUser(false);
  }, []);

  // Validate token on mount / token change
  useEffect(() => {
    let cancelled = false;
    async function validate() {
      if (!token) {
        setUser(false);
        setChecking(false);
        return;
      }
      setChecking(true);
      try {
        const res = await axios.get(`${API}/admin/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setUser(res.data);
      } catch {
        if (cancelled) return;
        localStorage.removeItem(STORAGE_KEY);
        setToken(null);
        setUser(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }
    validate();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = async (username, password) => {
    const res = await axios.post(`${API}/admin/login`, { username, password });
    const t = res.data.token;
    localStorage.setItem(STORAGE_KEY, t);
    setToken(t);
    setUser({ username: res.data.username });
    return res.data;
  };

  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  return (
    <AuthContext.Provider value={{ token, user, checking, login, logout, authHeader }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
